import React, { useMemo, useState } from 'react'
import LoadingSpinner from '../UI/LoadingSpinner'
import { artistKey, resolveLineup, toDayKey } from '../../utils/scheduleUtils'

/**
 * Bulk set-time entry for the whole lineup.
 *
 * Without this the planner is empty for every event that predates flyer set
 * times — which is all of them. Flyers often omit times entirely, so hand
 * entry is not a fallback path, it is the common one.
 *
 * Edits are staged locally and saved in one request. Per-row autosave would
 * mean a shared-lineup write on every keystroke.
 */
const SetTimeEditor = ({ event, stages = [], onSave, saving, onClose }) => {
  const lineup = useMemo(() => resolveLineup(event), [event])

  // Configured stages, plus any stage already sitting on a set so an existing
  // value can never become unselectable.
  const stageOptions = useMemo(() => {
    const seen = new Set(stages.map(stage => stage.toLowerCase()))
    const extra = []

    lineup.forEach(artist => {
      const stage = (artist.stage || '').trim()
      if (!stage || seen.has(stage.toLowerCase())) return
      seen.add(stage.toLowerCase())
      extra.push(stage)
    })

    return [...stages, ...extra]
  }, [stages, lineup])

  const [edits, setEdits] = useState(() => {
    const initial = {}
    lineup.forEach(artist => {
      initial[artistKey(artist.name)] = {
        startTime: artist.startTime || '',
        endTime: artist.endTime || '',
        stage: artist.stage || '',
        performanceDate: toDayKey(artist.performanceDate) || ''
      }
    })
    return initial
  })

  const [filter, setFilter] = useState('')

  const update = (key, field, value) => {
    setEdits(current => ({
      ...current,
      [key]: { ...current[key], [field]: value }
    }))
  }

  const visible = lineup.filter(artist =>
    !filter.trim() || artist.name.toLowerCase().includes(filter.trim().toLowerCase())
  )

  // Days the event spans, offered as a dropdown so multi-day festivals can
  // assign each set without anyone typing a date.
  const dayOptions = useMemo(() => {
    const start = event?.startDate ? new Date(event.startDate) : null
    const end = event?.endDate ? new Date(event.endDate) : start
    if (!start || isNaN(start.getTime())) return []

    const days = []
    const cursor = new Date(start)
    // Hard stop: a corrupt endDate should not spin here.
    while (cursor <= end && days.length < 30) {
      days.push(cursor.toISOString().split('T')[0])
      cursor.setDate(cursor.getDate() + 1)
    }
    return days
  }, [event])

  const handleSave = async () => {
    const ok = await onSave(edits)
    if (ok && onClose) onClose()
  }

  if (lineup.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p>This event has no lineup yet.</p>
        <p className="text-sm mt-1">
          Add artists by editing the event, or upload an updated flyer.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-300">
          Times are shared with everyone attending this event.
        </p>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter artists…"
          className="px-3 py-2 bg-white/10 border border-white/25 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="max-h-[26rem] overflow-y-auto space-y-2 pr-1">
        {visible.map(artist => {
          const key = artistKey(artist.name)
          const row = edits[key] || { startTime: '', endTime: '', stage: '', performanceDate: '' }

          return (
            <div key={key} className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="font-medium text-white mb-2 truncate">{artist.name}</div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <label className="text-xs text-gray-400">
                  Start
                  <input
                    type="time"
                    value={row.startTime}
                    onChange={(e) => update(key, 'startTime', e.target.value)}
                    className="mt-1 w-full px-2 py-1.5 bg-white/10 border border-white/25 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </label>

                <label className="text-xs text-gray-400">
                  End
                  <input
                    type="time"
                    value={row.endTime}
                    onChange={(e) => update(key, 'endTime', e.target.value)}
                    className="mt-1 w-full px-2 py-1.5 bg-white/10 border border-white/25 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </label>

                <label className="text-xs text-gray-400">
                  Stage
                  {/* A dropdown once stages are configured: free text here is
                      how you end up with "Main", "Main Stage" and "main stage"
                      as three separate rows on the grid. Any stage already on a
                      set is included so existing values are never silently
                      unselectable. */}
                  {stageOptions.length > 0 ? (
                    <select
                      value={row.stage}
                      onChange={(e) => update(key, 'stage', e.target.value)}
                      className="mt-1 w-full px-2 py-1.5 bg-white/10 border border-white/25 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="" className="bg-gray-900">—</option>
                      {stageOptions.map(stage => (
                        <option key={stage} value={stage} className="bg-gray-900">{stage}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={row.stage}
                      onChange={(e) => update(key, 'stage', e.target.value)}
                      placeholder="Main Stage"
                      className="mt-1 w-full px-2 py-1.5 bg-white/10 border border-white/25 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  )}
                </label>

                <label className="text-xs text-gray-400">
                  Day
                  {dayOptions.length > 1 ? (
                    <select
                      value={row.performanceDate}
                      onChange={(e) => update(key, 'performanceDate', e.target.value)}
                      className="mt-1 w-full px-2 py-1.5 bg-white/10 border border-white/25 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">—</option>
                      {dayOptions.map(day => (
                        <option key={day} value={day} className="bg-gray-900">
                          {new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', {
                            weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'
                          })}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="date"
                      value={row.performanceDate}
                      onChange={(e) => update(key, 'performanceDate', e.target.value)}
                      className="mt-1 w-full px-2 py-1.5 bg-white/10 border border-white/25 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  )}
                </label>
              </div>
            </div>
          )
        })}

        {visible.length === 0 && (
          <p className="text-center text-gray-400 py-6">No artists match "{filter}"</p>
        )}
      </div>

      <div className="flex gap-3 justify-end">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2 px-5 rounded-lg transition-all duration-200"
        >
          {saving ? <LoadingSpinner text="Saving…" /> : 'Save set times'}
        </button>
      </div>
    </div>
  )
}

export default SetTimeEditor
